/**
 * CSRF Error Handler
 * Detects CSRF token mismatches (400 errors) and displays user-friendly messages
 */

const CSRF_ERROR_MESSAGE = 'Your browser session has expired. Please refresh the page and resubmit the form. Make sure cookies are enabled.';

/**
 * Check if an error response is likely a CSRF mismatch
 * @param {Object} error - Axios error object
 * @returns {boolean}
 */
function isCsrfError(error) {
    if (!error.response) {
        return false;
    }

    const status = error.response.status;

    // CSRF mismatches in Craft CMS return 400 Bad Request
    // But we need to check the actual error message to avoid false positives
    if (status === 400) {
        // Check response data/message for CSRF-specific error messages
        const responseData = error.response.data || '';
        let errorMessage = '';
        
        // First try to get error message without stringifying (most common case)
        if (typeof responseData === 'string') {
            errorMessage = responseData;
        } else if (responseData && typeof responseData === 'object') {
            // Check common error message properties first (avoid expensive stringify if possible)
            errorMessage = responseData.message || responseData.error || '';
            
            // Only stringify if we didn't find a message property and need to check the full response
            if (!errorMessage) {
                // Only stringify as a last resort - this is expensive for large responses
                try {
                    errorMessage = JSON.stringify(responseData);
                } catch (e) {
                    // If stringify fails, return false (not a CSRF error we can detect)
                    return false;
                }
            }
        }
        
        // If we still don't have a message, it's not a CSRF error we can detect
        if (!errorMessage) {
            return false;
        }
        
        // Craft CMS CSRF errors typically contain these phrases
        const csrfErrorIndicators = [
            'Unable to verify your data submission',
            'CSRF',
            'csrf',
            'verify your data',
            'data submission'
        ];
        
        // Only treat as CSRF error if the message contains CSRF-related text
        const lowerErrorMessage = errorMessage.toLowerCase();
        const isActualCsrfError = csrfErrorIndicators.some(indicator => 
            lowerErrorMessage.includes(indicator.toLowerCase())
        );
        
        return isActualCsrfError;
    }

    return false;
}

/**
 * Display a CSRF error message to the user
 * Uses persistent mode since user needs to refresh the page
 */
function showCsrfError() {
    showError(CSRF_ERROR_MESSAGE, true);
}

/**
 * Display an error message in the notification bar
 * @param {string} message - Error message to display
 * @param {boolean} persistent - If true, error will not auto-hide (default: false)
 */
function showError(message, persistent = false) {
    const notificationBar = document.getElementById('notification-bar');

    if (notificationBar) {
        notificationBar.classList.remove('hidden', 'success', 'error', 'error-persistent');
        notificationBar.classList.add(persistent ? 'error-persistent' : 'error');
        notificationBar.innerHTML = `<p>${message}</p>`;

        // Scroll to top so user sees the error
        window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
        // Fallback to alert if notification bar not found
        alert(message);
    }
}

/**
 * Hide the notification bar
 */
function hideError() {
    const notificationBar = document.getElementById('notification-bar');

    if (notificationBar) {
        notificationBar.classList.add('hidden');
        notificationBar.classList.remove('error', 'error-persistent', 'success');
        notificationBar.innerHTML = '';
    }
}

/**
 * Create an axios response interceptor for CSRF error handling
 * 
 * This interceptor only handles CSRF errors for non-Formie requests.
 * Formie forms have their own error handling mechanism and CSRF refresh logic.
 * 
 * @param {Object} axiosInstance - Axios instance to attach interceptor to
 */
function attachCsrfErrorInterceptor(axiosInstance) {
    axiosInstance.interceptors.response.use(
        // Success handler - pass through
        (response) => response,
        // Error handler
        (error) => {
            // Skip CSRF error handling for Formie form submissions
            // Formie has its own error handling mechanism and CSRF token refresh logic
            const requestUrl = error.config?.url || '';
            const isFormieRequest = requestUrl.includes('formie') || 
                                   requestUrl.includes('actions/formie');
            
            if (!isFormieRequest && isCsrfError(error)) {
                console.error('CSRF token mismatch detected:', error);
                showCsrfError();
            }

            // Re-throw the error so individual catch handlers still work
            return Promise.reject(error);
        }
    );
}

export {
    isCsrfError,
    showCsrfError,
    showError,
    hideError,
    attachCsrfErrorInterceptor,
    CSRF_ERROR_MESSAGE
};
